import { cloneDeep, pick, merge, map } from 'lodash';
import { EventEmitter } from '../../utils/event-emitter';

const MAX_HISTORY_LENGTH = 100;

/** @type {BadgeEditor.State} */
export const BLANK_STATE = {
    blocks: {
        images: [],
        text: [],
        qrcode: [],
        background: {},
    },
};

export class EditorState extends EventEmitter {
    /**
     * @param {BadgeEditor.StateOrEmpty} initialState
     */
    constructor(initialState) {
        super();

        /** @type {BadgeEditor.State} */
        const state = merge(initialState, BLANK_STATE);

        /** @type {BadgeEditor.State[]} */
        this.history = [state];

        /** @type {number} */
        this._currentIndex = 0;
    }

    /**
     * Returns current state.
     */
    get current() {
        return this.history[this._currentIndex];
    }

    /**
     * Create a new point in history
     *
     * @param {(state: BadgeEditor.State) => void} callback Function that is called with new deep-cloned state
     * @emits EditorState#state
     */
    update(callback) {
        const newState = cloneDeep(this.current);

        callback(newState);

        this.history.splice(0, this._currentIndex, newState);
        this._currentIndex = 0;

        if (this.history.length > MAX_HISTORY_LENGTH) {
            this.history.splice(MAX_HISTORY_LENGTH, this.history.length - MAX_HISTORY_LENGTH);
        }

        this.emit('state', newState);
    }

    /** @type {(state: BadgeEditor.State) => void} */
    replace(state) {
        const newState = cloneDeep(state);

        this.history = [newState];
        this._currentIndex = 0;

        this.emit('state', newState);
    }

    /**
     * Returns all block Id's in current state
     *
     * @returns {string[]}
     */
    getIds() {
        const blocks = this.current.blocks;
        return ['text', 'images', 'qrcode']
            .reduce((result, key) => result.concat(map(blocks?.[key], '_id') ?? []), []);
    }

    /**
     * Whether state can be undoed
     */
    get canUndo() {
        return this._currentIndex < this.history.length - 1;
    }

    /**
     * Undo to previous state
     *
     * @emits EditorState#state
     */
    undo() {
        this._currentIndex = Math.min(this._currentIndex + 1, this.history.length - 1);

        this.emit('state', this.current);
    }

    /**
     * Whether state can be redoed
     */
    get canRedo() {
        return this._currentIndex > 0;
    }

    /**
     * Redo to next state
     *
     * @emits EditorState#state
     */
    redo() {
        this._currentIndex = Math.max(this._currentIndex - 1, 0);

        this.emit('state', this.current);
    }
}

const BASE_PROPS = [
    '_id',
    'id',
    'top',
    'left',
    'width',
    'height',
    'angle',
];

const TRANSFORM_PROPS = [
    'scaleX',
    'scaleY',
    'flipX',
    'flipY',
];

export const TEXTBOX_PROPS = [
    ...BASE_PROPS,
    'fill',
    'text',
    'fontSize',
    'maxFontSize',
    'minFontSize',
    'useDynamicFontSize',
    'textAlign',
    'fontFamily',
];

export const IMAGE_PROPS = [
    ...BASE_PROPS,
    ...TRANSFORM_PROPS,
    'isImage',
];

export const QR_CODE_PROPS = [
    ...BASE_PROPS,
    ...TRANSFORM_PROPS,
    'isQrCode',
    'content',
];

/** @type {(json: Record<string, unknown>) => BadgeEditor.Textbox} */
export function sanitizeTextboxProps(json) {
    return pick(json, TEXTBOX_PROPS);
}

/** @type {(json: Record<string, unknown>) => BadgeEditor.Image} */
export function sanitizeImageProps(json) {
    return pick(json, IMAGE_PROPS);
}

/** @type {(json: Record<string, unknown>) => BadgeEditor.QrCode} */
export function sanitizeQrCodeProps(json) {
    return pick(json, QR_CODE_PROPS);
}
